<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { Helper, Input } from "flowbite-svelte-v1";
	import { createEventDispatcher } from "svelte";
	import { DatePropertiesSchema, InputType, type DateProperties, type Field } from "#gen/forms/v1/field_pb.js";
	import { getValidatorWithErrors } from "#lib/ajv.js";
	import DefaultValue from "#lib/components/page/components/form/DefaultValue.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { FieldProps, TypeIcon } from "#lib/components/page/components/form/FieldProps.js";
	import MultiFieldDateInput from "#lib/components/page/components/form/MultiFieldDateInput.svelte";
	import { getDefaultTextAsStruct, getDefaultTextValue } from "#lib/components/page/components/form/defaultValue.js";
	import { isCompleteDate, validateDate } from "#lib/date.js";
	import { ERR_REQUIRED } from "#lib/validation.js";

	type $$Props = FieldProps;
	interface $$Events {
		input: CustomEvent<Field>;
		change: CustomEvent<Field>;
	}

	const dispatch = createEventDispatcher();

	export let typeIcon: TypeIcon | undefined = undefined;
	export let field: Field;
	export let editable = false;
	export let active = false;
	export let value: string | (string[] & string) | undefined = !editable ? getDefaultTextValue(field.defaultValue) : undefined;
	export let readOnly = false;

	const inputDisabled = (editable && field.immutable) || readOnly || !field.active;
	let error: string | undefined = undefined;

	$: !editable && (value = getDefaultTextValue(field.defaultValue));
	$: properties = field.properties?.properties.case === "date" ? (field.properties?.properties.value as DateProperties) : create(DatePropertiesSchema, {});

	export async function validate(): Promise<boolean> {
		if (field.required && !value) {
			error = ERR_REQUIRED;
			return false;
		} else if (value) {
			const dateError = validateDate(value);
			if (dateError) {
				error = dateError;
				return false;
			}
		}
		if (field.validationSchema?.length > 0 && value) {
			const validator = await getValidatorWithErrors(field.validationSchema);
			const isValid = validator(value);
			if (!isValid) {
				error = validator.errors?.[0]?.message ?? "";
				return false;
			}
		}
		error = "";
		return true;
	}

	function handleValueChange(event: Event) {
		const target = event.target as HTMLInputElement;
		if (!editable) {
			value = target.value;
			// Don't validate on input - only validate on blur or submit
		} else {
			field.defaultValue = getDefaultTextAsStruct(target.value);
			dispatch("input", field);
		}
	}

	function handleFieldChange() {
		if (editable) {
			dispatch("change", field);
		}
	}

	function handleBlur() {
		if (!editable) {
			if (properties.inputType === InputType.MULTIPLE) {
				if (isCompleteDate(value)) {
					validate();
				}
			} else {
				validate();
			}
		}
	}
</script>

<div {...$$restProps}>
	{#if editable}
		<FieldHeader {typeIcon} {active} {field} on:input on:change />
	{:else}
		<label for={field.name} class="mb-2 inline-block text-sm font-medium" class:required={field.required}>
			{field.title}
		</label>
		{#if field.description}
			<div class="field-description my-2 text-xs text-gray-500">{field.description}</div>
		{/if}
	{/if}
	{#if field.static && !editable}
		<DefaultValue>{getDefaultTextValue(field.defaultValue)}</DefaultValue>
	{:else}
		{#if editable || properties.inputType !== InputType.MULTIPLE}
			<div class="relative">
				<Input
					class={`${readOnly ? "date-disabled disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}${value ? "" : " date-empty"}`}
					color={error ? "red" : "default"}
					value={value ?? getDefaultTextValue(field.defaultValue)}
					type="date"
					name={field.name}
					id={field.name}
					disabled={inputDisabled}
					onblur={handleBlur}
					oninput={handleValueChange}
					onchange={handleFieldChange}
				/>
			</div>
		{:else}
			<MultiFieldDateInput class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} color={error ? "red" : "default"} value={value ?? getDefaultTextValue(field.defaultValue)} disabled={inputDisabled} onBlur={handleBlur} onInput={handleValueChange} onChange={handleFieldChange} />
		{/if}
		{#if error}
			<Helper class="mt-2 text-red-700"><span class="font-medium">{error}</span></Helper>
		{/if}
	{/if}
</div>
